// src/views/services/categories/InsertServiceCategory.jsx
import React, { useRef, useState } from "react";
import {
  Box,
  Button,
  Input,
  FormControl,
  FormLabel,
  Text,
  SimpleGrid,
  useToast,
  Switch,
  HStack,
  Badge,
  Textarea,
  Spinner,
} from "@chakra-ui/react";
import Card from "components/card/Card";
import { motion } from "framer-motion";
import fetchWithToken from "views/auth/signIn/axiosInstance";
import { useTranslation } from "react-i18next";

const InsertServiceCategory = ({ serviceId, onSuccess }) => {
  const { t } = useTranslation();
  const toast = useToast();
  const imageInputRef = useRef(null);

  const [name, setName] = useState("");
  const [translationKey, setTranslationKey] = useState("");
  const [translations, setTranslations] = useState({ fr: "", it: "" });
  const [image, setImage] = useState("");
  const [imageSize, setImageSize] = useState(null);
  const [status, setStatus] = useState(true); // boolean
  const [submitting, setSubmitting] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const pageId = 13;

  const validateImageSize = (file) => file.size <= 1024 * 1024;

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) {
      setImage("");
      setImageSize(null);
      return;
    }
    setImageSize(file.size);
    if (!validateImageSize(file)) {
      toast({
        title: t("Image must be less than 1MB"),
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
      return;
    }
    const reader = new FileReader();
    reader.onloadend = () => setImage(reader.result);
    reader.readAsDataURL(file);
  };

  const handleTranslationChange = (lang, value) => {
    setTranslations({ ...translations, [lang]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!serviceId) {
      toast({
        title: t("Service is required"),
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
      return;
    }

    setSubmitting(true);
    const payload = {
      name,
      translation_key: translationKey,
      service_id: serviceId,
      translations,
      status, // boolean true/false
      ...(image ? { image } : {}),
      page_id: pageId,
    };

    try {
      await fetchWithToken(
        `${process.env.REACT_APP_API_URL}/service-categories`,
        "POST",
        payload
      );

      toast({
        title: t("Category created successfully"),
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });

      // reset
      setName("");
      setTranslationKey("");
      setTranslations({ fr: "", it: "" });
      setImage("");
      setStatus(true);
      if (imageInputRef.current) imageInputRef.current.value = "";

      if (onSuccess) onSuccess();
    } catch (error) {
      toast({
        title: t("Failed to create category"),
        description: error.message || t("Could not create category"),
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: -25 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35 }}
    >
      <Card p={{ base: "18px", md: "24px" }}>
        <form onSubmit={handleSubmit}>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
            {/* Category Name */}
            <FormControl isRequired isInvalid={!!fieldErrors.name}>
              <FormLabel color="blue.600">{t("Category Name")}</FormLabel>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t("Enter category name")}
              />
            </FormControl>

            {/* Translation Key */}
            <FormControl isRequired isInvalid={!!fieldErrors.translationKey}>
              <FormLabel color="blue.600">{t("Translation Key")}</FormLabel>
              <Input
                value={translationKey}
                onChange={(e) => setTranslationKey(e.target.value)}
                placeholder={t("Enter translation key")}
              />
            </FormControl>

            {/* French Translation */}
            <FormControl>
              <FormLabel color="blue.600">
                {t("Translation ")}
                <Text as="span" fontWeight="bold">
                  {t("(French)")}
                </Text>
              </FormLabel>
              <Input
                value={translations.fr}
                onChange={(e) => handleTranslationChange("fr", e.target.value)}
                placeholder={t("Enter French name")}
              />
            </FormControl>

            {/* Italian Translation */}
            <FormControl>
              <FormLabel color="blue.600">
                {t("Translation ")}
                <Text as="span" fontWeight="bold">
                  {t("(Italian)")}
                </Text>
              </FormLabel>
              <Input
                value={translations.it}
                onChange={(e) => handleTranslationChange("it", e.target.value)}
                placeholder={t("Enter Italian name")}
              />
            </FormControl>

            {/* Image Upload */}
            <FormControl isInvalid={!!fieldErrors.image} isRequired>
              <FormLabel color="blue.600">{t("Category Image")}</FormLabel>
              <Input
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                ref={imageInputRef}
              />
               {/* Always show the helper text */}
    <Text fontSize="sm" color="gray.500" mt={2}>
      {t("Recommended size: Max 1 MB (≈1024 KB), up to 1024×1024 pixels")}
    </Text>
              {imageSize !== null && (
                <Text
                  fontSize="xs"
                  color={imageSize > 1024 * 1024 ? "red.500" : "gray.500"}
                  mt={1}
                >
                  {t("Selected file size")}: {(imageSize / 1024).toFixed(1)} KB
                  {imageSize > 1024 * 1024 && ` (${t("Too large")})`}
                </Text>
              )}
            </FormControl>

            {/* Status */}
            <FormControl isRequired>
              <FormLabel color="blue.600">{t("Status")}</FormLabel>
              <HStack spacing={4}>
                <Switch
                  isChecked={status}
                  onChange={(e) => setStatus(e.target.checked)}
                  colorScheme="cyan"
                />
                <Badge
                  colorScheme={status ? "green" : "red"}
                  variant="solid"
                  px={3}
                  py={1}
                  borderRadius="md"
                >
                  {status ? t("Active") : t("Inactive")}
                </Badge>
              </HStack>
            </FormControl>
          </SimpleGrid>

          {/* Image Preview */}
          {image && (
            <Box mt={4}>
              <Text mb={2} fontWeight="bold">
                {t("Preview")}
              </Text>
              <img
                src={image}
                alt="preview"
                style={{ maxHeight: "120px", borderRadius: "8px" }}
              />
            </Box>
          )}

          {/* Submit Button */}
          <Button
            mt={5}
            colorScheme="cyan"
            color="white"
            type="submit"
            isLoading={submitting}
            borderRadius="16px"
            w={{ base: "100%", md: "148px" }}
            h="46px"
          >
            {t("Submit")}
          </Button>
        </form>
      </Card>
    </motion.div>
  );
};

export default InsertServiceCategory;
